<script>
	import { cn } from "./cn.js";
	let className = "";
	export { className as class };

	/**
	 * ⚠️⚠️ **포커스된 칸은 바깥 값으로 덮어쓰지 않는다 — 한글이 안 써지는 원인이었다.**
	 *
	 * 예전엔 `value` 를 `$$restProps` 로 그냥 흘려보냈다. 그러면 Svelte 가 갱신 때마다
	 * `node.value = …` 를 다시 쓰는데, **조합(composition) 중에 .value 를 대입하면 브라우저가
	 * 조합을 끊는다.** 한 글자 칠 때마다 끊겨서 "안녕" 이 **"ㅇㅏㄴㄴㅕㅇ"** 처럼 낱자로 쌓인다
	 * (런처 빌더 '받을 정보' 라벨에 `ㅇㅇㄴㅁㅇㄴㅁ…` 이 실제로 저장돼 있었다).
	 * 영문은 조합이 없어서 멀쩡하다 — 그래서 **"한글만 입력이 안 된다"** 로 보인다.
	 *
	 * → `value` 를 스프레드에서 빼내고, **지금 치고 있는 칸이 아닐 때만** 반영한다.
	 *
	 * ⚠️ `compositionstart/end` 플래그로 막는 방법은 **틀렸다.** 브라우저가 `compositionend`
	 *    **다음에** `input` 을 쏘기 때문에, end 에서 플래그를 내리면 그 사이에 옛 값(마지막 조합
	 *    중간값)으로 되돌려 써서 완성된 글자가 날아간다. 포커스로 가르면 그 순서 문제가 없다.
	 * ⚠️ 첫 렌더에는 포커스가 없으니 초기값이 정상적으로 들어간다. 자동 포커스 칸까지 챙기려고
	 *    `first` 를 둔다(한 번은 무조건 쓴다).
	 * ⚠️ `on:input` 은 그대로 **전달(forward)** 한다 — 여기서 가로채 다시 쏘면 `e.target` 이
	 *    사라져 호출부의 `e.target.value` 가 전부 깨진다.
	 */
	let el;
	let first = true;
	$: ({ value, ...rest } = $$restProps);
	$: if (el && value !== undefined) {
		const next = String(value ?? "");
		if ((first || document.activeElement !== el) && el.value !== next) el.value = next;
		first = false;
	}

	/**
	 * ⚠️⚠️ **호출부가 폭 클래스(`tw-w-*`)를 주면 기본 `tw-w-full` 을 뺀다.**
	 *
	 * `cn` 은 tailwind-merge 가 아니라 단순 결합이라 두 클래스가 **함께 남는다.** 특이도가 같으니
	 * 스타일시트에서 **뒤에 오는 쪽**이 이기는데, 빌드물에서 `.tw-w-full` 이 `.tw-w-28` 보다
	 * 뒤에 있다(실측: 5754자 vs 6702자) → 호출부의 폭 지정이 **조용히 무시되고 100% 가 된다.**
	 *
	 * 증상이 '폭'으로 안 보인다: flex 한 줄에서 그 칸이 100% 를 요구하면 여유공간이 음수가 되고,
	 * 줄이는 몫은 basis 비례로 나뉘어 **`flex-1 min-w-0` 인 옆 칸이 0 폭으로 찌그러진다**
	 * (basis 가 0 이라 줄일 게 없다) → 그 칸은 **안 보이고 클릭도 안 돼 입력이 불가능**해 보인다.
	 * 런처 빌더 [신원] '받을 정보' 의 이름(라벨) 칸이 이것이었다(키 칸 `tw-w-28` 이 100% 를 먹었다).
	 *
	 * ⚠️ `tw-min-w-0` / `tw-max-w-*` 는 폭 지정이 아니다 — 그것만 준 호출부(`flex-1` 과 같이 쓰는
	 *    자리)는 `tw-w-full` 이 그대로 필요하다. 그래서 경계를 `tw-w-` 로만 둔다.
	 * ⚠️ `Select.svelte` 는 이미 같은 가드를 갖고 있다 — 여기만 빠져 있었다.
	 */
	$: callerSetsWidth = /(?:^|\s)tw-w-/.test(className);
</script>

<textarea
	bind:this={el}
	class={cn(
		"tw-flex tw-min-h-[80px] tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-2 tw-text-sm tw-text-foreground placeholder:tw-text-muted-foreground focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring disabled:tw-opacity-50",
		!callerSetsWidth && "tw-w-full",
		className,
	)}
	on:input
	on:change
	on:blur
	on:focus
	on:keydown
	{...rest}
/>
